/**
 * Service de atendimento (prontuário) — Central de Amparo.
 * Persiste o prontuário no Supabase na tabela cadastrada em support_center_forms
 * e marca o agendamento como realizado. Não usa API externa.
 */
import { supabase } from '../lib/supabase'
import { supportCenterAppointmentsService } from './supportCenterAppointments'
import { supportCenterFormsService } from './supportCenterForms'
import type { SupportCenterFormKind } from '../types/supportCenterSpecialties'
import type { SupportCenterPsychAttendancePayload } from '../types/supportCenterPsychAttendance'

const ATTENDANCE_TEXT_COLUMN_BY_KIND: Partial<
  Record<Exclude<SupportCenterFormKind, 'psicologico'>, string>
> = {
  nutricional: 'observations',
  fitness: 'observations',
  juridico: 'guidance',
  social: 'guidance',
}

function attendanceTextColumn(formKind: SupportCenterFormKind): string | null {
  if (formKind === 'psicologico') return null
  return ATTENDANCE_TEXT_COLUMN_BY_KIND[formKind] ?? null
}

export const supportCenterAttendService = {
  async submitPsychAttendance(payload: SupportCenterPsychAttendancePayload): Promise<void> {
    const table = await supportCenterFormsService.getAttendanceTableName('psicologico')
    const { error } = await supabase.from(table).insert({
      appointment_id: payload.appointment_id,
      patient_cpf: payload.patient_cpf,
      holder_cpf: payload.holder_cpf,
      specialty_id: payload.specialization_id || null,
      professional_user_id: payload.professional_user_id,
      patient_name: payload.patient_name,
      insured_number: payload.insured_number,
      attendance_date: payload.attendance_date,
      attendance_number: payload.attendance_number,
      psychologist_name: payload.psychologist_name,
      demand_notes: payload.demand_notes,
      intervention_notes: payload.intervention_notes,
      patient_response: payload.patient_response,
      emotional_state: payload.emotional_state,
      risk_indicator: payload.risk_indicator,
      referral: payload.referral,
      next_contact_date: payload.next_contact_date,
      additional_notes: payload.additional_notes,
    })

    if (error) throw error

    await supportCenterAppointmentsService.markRealizado(payload.appointment_id)
  },

  async getPsychologicalAttendance(appointmentId: string): Promise<{
    demand_notes: string | null
    intervention_notes: string | null
    patient_response: string | null
    emotional_state: string | null
    risk_indicator: string | null
    referral: string | null
    additional_notes: string | null
  } | null> {
    const table = await supportCenterFormsService.getAttendanceTableName('psicologico')
    const { data, error } = await supabase
      .from(table)
      .select(
        'demand_notes, intervention_notes, patient_response, emotional_state, risk_indicator, referral, additional_notes'
      )
      .eq('appointment_id', appointmentId)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    const row = data as Record<string, string | null>
    return {
      demand_notes: row.demand_notes ?? null,
      intervention_notes: row.intervention_notes ?? null,
      patient_response: row.patient_response ?? null,
      emotional_state: row.emotional_state ?? null,
      risk_indicator: row.risk_indicator ?? null,
      referral: row.referral ?? null,
      additional_notes: row.additional_notes ?? null,
    }
  },

  async getAttendanceNotes(
    formKind: SupportCenterFormKind,
    appointmentId: string
  ): Promise<string | null> {
    const column = attendanceTextColumn(formKind)
    if (!column) return null

    const table = await supportCenterFormsService.getAttendanceTableName(formKind)
    const { data, error } = await supabase
      .from(table)
      .select(column)
      .eq('appointment_id', appointmentId)
      .maybeSingle()

    if (error) throw error
    const row = data as Record<string, string | null> | null
    const notes = row?.[column]?.trim()
    return notes || null
  },

  async submitAttendanceNotes(
    formKind: SupportCenterFormKind,
    appointmentId: string,
    notes: string
  ): Promise<void> {
    const column = attendanceTextColumn(formKind)
    if (!column) throw new Error('Formulário de atendimento não suportado.')

    const text = notes.trim()
    if (!text) throw new Error('Informe o texto do atendimento.')

    const table = await supportCenterFormsService.getAttendanceTableName(formKind)
    const { error } = await supabase.from(table).insert({
      appointment_id: appointmentId,
      [column]: text,
    })

    if (error) throw error

    await supportCenterAppointmentsService.markRealizado(appointmentId)
  },
}
